import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useStore, useDeliveryFacts } from "./StoreContext";
import { readFilters, writeFilters, applyFilters, activeFilterCount, priceInfo } from "./shopData";
import { money, dropDate } from "./format";
import ProductCard from "./ProductCard";
import Filters from "./Filters";
import { CountdownDigits, NotifyForm } from "./Countdown";
import useSeo from "./useSeo";
import { itemListLd } from "./structuredData";
import Icon from "./Icons";
import { contentOf, isExternal } from "./storeContent";

// The site's own film grain (public/, so not bundled).
const GRAIN = { backgroundImage: "url(/images/grain.webp)" };

// The store front: one hero (what the CMS says; by default next drop ›
// featured collection › sale › featured piece), then the grid with its filters. A collection page is the
// same view, scoped, with the collection's story on top.
export default function ShopHome() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const { products, collections, sales, catalog, error, refresh, config } = useStore();
  const content = contentOf(config);
  const filters = readFilters(params);
  const collection = slug ? collections.find((c) => c.slug === slug) : null;
  const scope = useMemo(() => (slug ? products.filter((p) => p.collection?.slug === slug) : products), [products, slug]);
  const shown = useMemo(() => applyFilters(scope, filters), [scope, filters]);
  const browsing = !!(filters.q || filters.category || activeFilterCount(filters));
  const setFilters = (f) => setParams(writeFilters(f), { replace: true, preventScrollReset: true });

  useSeo(
    collection
      ? {
          title: collection.name,
          description: collection.tagline || collection.story || `The ${collection.name} collection by The Creaks.`,
          image: collection.imageUrl,
          path: `/merch/collection/${collection.slug}`,
        }
      : {
          title: filters.q ? `Search: ${filters.q}` : null,
          description: "Official merch from The Creaks, the Prague cinematic rock band. T-shirts, records and more, shipped from Prague across the EU.",
          image: products[0]?.images[0]?.url,
          path: "/merch",
          noindex: browsing,
          jsonLd: products.length ? itemListLd(products) : null,
        }
  );

  if (slug && catalog && !collection) {
    return (
      <div className="sh-wrap sh-empty">
        <h1 className="sh-h1">This collection isn't here anymore</h1>
        <Link className="sh-btn" to="/merch">
          See everything
        </Link>
      </div>
    );
  }

  return (
    <>
      {collection ? <CollectionHead collection={collection} onDrop={refresh} /> : !browsing && catalog && <Hero hero={content.hero} products={products} collections={collections} sales={sales} onDrop={refresh} setFilters={(p) => setFilters({ ...filters, ...p })} />}

      <section className="sh-wrap sh-shop" id="shop" aria-label="Products">
        {filters.q && <h1 className="sh-h1 sh-results">Results for “{filters.q}”</h1>}
        {!collection && !filters.q && <h1 className="sh-sr">The Creaks merch</h1>}
        {catalog && <Filters products={scope} filters={filters} onChange={setFilters} total={shown.length} />}
        {error && !catalog && (
          <div className="sh-empty">
            <p>The store didn't load. {error}</p>
            <button className="sh-btn" onClick={refresh}>
              Try again
            </button>
          </div>
        )}
        {!catalog && !error && <GridSkeleton />}
        {catalog && shown.length > 0 && (
          <div className="sh-grid">
            {shown.map((p, i) => (
              <ProductCard key={p.id} product={p} eager={i < 4} />
            ))}
          </div>
        )}
        {catalog && !shown.length && (
          <div className="sh-empty">
            <p>{scope.length ? "Nothing matches that." : "Nothing here yet. Check back soon."}</p>
            {scope.length > 0 && (
              <button className="sh-btn sh-btn-ghost" onClick={() => setFilters({ ...readFilters(new URLSearchParams()), sort: filters.sort })}>
                Clear filters
              </button>
            )}
          </div>
        )}
      </section>

      {!browsing && content.sections.collections && collections.filter((c) => c.slug !== slug).length > 0 && (
        <section className="sh-wrap sh-collections" aria-label="Collections">
          <h2 className="sh-h2">{collection ? "More collections" : "Collections"}</h2>
          <div className="sh-colrow">
            {collections
              .filter((c) => c.slug !== slug)
              .map((c) => (
                <Link key={c.slug} to={`/merch/collection/${c.slug}`} className="sh-coltile">
                  {c.imageUrl ? <img src={c.imageUrl} alt="" loading="lazy" decoding="async" /> : <span className="sh-coltile-blank" />}
                  <span className="sh-coltile-text">
                    <span className="sh-eyebrow">{c.teased ? `Drops ${dropDate(c.launchAt)}` : `${c.count} ${c.count === 1 ? "piece" : "pieces"}`}</span>
                    <span className="sh-display sh-coltile-name">{c.name}</span>
                  </span>
                </Link>
              ))}
          </div>
        </section>
      )}

      {content.trust.show && <TrustStrip items={content.trust.items} />}
    </>
  );
}

// What the banner shows. The CMS picks (collection / product / sale / custom);
// a choice that can't be honoured (the collection is gone, no sale running, a
// custom banner with nothing in it) falls back to the automatic order.
function heroView(hero, { products, collections, sales, setFilters }) {
  const dropOf = (c) => ({
    image: c.imageUrl,
    eyebrow: "Next drop",
    title: c.name,
    text: c.tagline,
    drop: { launchAt: c.launchAt, topic: `drop:${c.slug}`, to: `/merch/collection/${c.slug}` },
  });
  const collectionView = (c) =>
    c.teased ? dropOf(c) : { image: c.imageUrl, title: c.name, text: c.tagline, cta: { label: "Shop the collection", to: `/merch/collection/${c.slug}` } };
  const productView = (p) =>
    p.teased
      ? {
          image: p.images[0]?.url,
          eyebrow: "Next drop",
          title: p.title,
          text: p.description?.split("\n")[0],
          drop: { launchAt: p.launchAt, topic: `drop:${p.collection?.slug || p.slug}`, to: `/merch/${p.slug}` },
        }
      : { image: p.images[0]?.url, eyebrow: "Featured", title: p.title, text: p.description?.split("\n")[0], cta: { label: `Shop now · ${money(priceInfo(p).min)}`, to: `/merch/${p.slug}` } };

  const next = collections.filter((c) => c.teased).sort((a, b) => String(a.launchAt).localeCompare(String(b.launchAt)))[0];
  const featured = collections.find((c) => c.featured && !c.teased);
  const maxOff = Math.max(0, ...products.map((p) => priceInfo(p).off));
  const sale = maxOff > 0 ? sales[0] || { name: "Sale" } : null;
  const piece = products.find((p) => p.featured && p.images.length) || products.find((p) => p.images.length);
  const saleView = () =>
    sale && {
      image: piece?.images[0]?.url,
      eyebrow: sale.name,
      title: `Up to ${maxOff} % off`,
      text: sale.endsAt ? `Until ${dropDate(sale.endsAt)}` : null,
      cta: {
        label: "Shop the sale",
        onClick: () => {
          setFilters({ sale: true });
          document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" });
        },
      },
    };

  let view = null;
  if (hero.mode === "collection") {
    const c = collections.find((x) => x.slug === hero.collectionSlug);
    view = c ? collectionView(c) : null;
  } else if (hero.mode === "product") {
    const p = products.find((x) => x.slug === hero.productSlug);
    view = p ? productView(p) : null;
  } else if (hero.mode === "sale") view = saleView();
  else if (hero.mode === "custom" && (hero.title || hero.imageUrl)) view = { image: null, eyebrow: "", title: "", text: "", cta: null };
  if (!view) view = (next && dropOf(next)) || (featured && collectionView(featured)) || saleView() || (piece && productView(piece)) || null;
  if (!view) return null;

  // The CMS's own words and picture win wherever they're filled in.
  return {
    ...view,
    image: hero.imageUrl || view.image,
    eyebrow: hero.eyebrow || view.eyebrow,
    title: hero.title || view.title,
    text: hero.text || view.text,
    cta: hero.ctaLabel && hero.ctaHref ? { label: hero.ctaLabel, href: hero.ctaHref } : view.cta,
  };
}

function Cta({ cta, className }) {
  if (cta.onClick) return <button className={className} onClick={cta.onClick}>{cta.label}</button>;
  if (cta.href && isExternal(cta.href)) {
    return (
      <a className={className} href={cta.href} target="_blank" rel="noreferrer">
        {cta.label}
      </a>
    );
  }
  return (
    <Link className={className} to={cta.to || cta.href}>
      {cta.label}
    </Link>
  );
}

function Hero({ hero, products, collections, sales, onDrop, setFilters }) {
  if (hero.mode === "off") return null;
  const view = heroView(hero, { products, collections, sales, setFilters });
  if (!view) return null;
  const { drop, cta } = view;
  return (
    <HeroFrame view={view} hero={hero}>
      {drop && hero.showCountdown && (
        <>
          <CountdownDigits to={drop.launchAt} onDone={onDrop} />
          <p className="sh-hero-when">{dropDate(drop.launchAt)} · Prague time</p>
        </>
      )}
      {(drop || cta) && (
        <div className="sh-hero-actions">
          {drop && <NotifyForm topic={drop.topic} cta="Notify me" compact note={null} />}
          {drop && !cta && (
            <Link className="sh-textlink" to={drop.to}>
              Preview the drop <Icon name="arrow" size={16} />
            </Link>
          )}
          {cta && (
            <Cta cta={cta} className={drop ? "sh-textlink" : "sh-btn"} />
          )}
        </div>
      )}
    </HeroFrame>
  );
}

// `overlay` is the veil's darkness in %; 55 is the original look.
function veil(overlay) {
  const k = overlay / 55;
  const a = (x) => Math.min(1, +(x * k).toFixed(3));
  return `linear-gradient(to top, rgba(0,0,0,${a(0.94)}) 0%, rgba(0,0,0,${a(0.45)}) 42%, rgba(0,0,0,${a(0.12)}) 100%), radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,${a(0.55)}) 100%)`;
}

function HeroFrame({ view, hero, children }) {
  const { image, eyebrow, title, text } = view;
  return (
    <section className={`sh-hero${image ? "" : " no-image"}`} data-align={hero.align} data-height={hero.height}>
      {image && <img className="sh-hero-img" src={image} alt="" fetchPriority="high" />}
      <div className="sh-hero-veil" style={{ background: veil(hero.overlay) }} />
      <div className="sh-grain" style={GRAIN} />
      <div className="sh-hero-inner">
        {eyebrow && <p className="sh-eyebrow">{eyebrow}</p>}
        {title && (
          <h2 className="sh-display sh-hero-title" data-face={hero.font === "inherit" ? undefined : hero.font}>
            {title}
          </h2>
        )}
        {text && <p className="sh-hero-text">{text}</p>}
        {children}
      </div>
    </section>
  );
}

function CollectionHead({ collection: c, onDrop }) {
  const paragraphs = String(c.story || "").split(/\n\s*\n/).filter(Boolean);
  return (
    <header className="sh-colhead">
      {c.imageUrl && <img className="sh-colhead-img" src={c.imageUrl} alt="" fetchPriority="high" />}
      <div className="sh-hero-veil" />
      <div className="sh-grain" style={GRAIN} />
      <div className="sh-wrap sh-colhead-inner">
        <nav className="sh-crumbs" aria-label="Breadcrumb">
          <Link to="/merch">Merch</Link>
          <span>/</span>
          <span>Collections</span>
        </nav>
        {c.teased && <p className="sh-eyebrow">Next drop</p>}
        <h1 className="sh-display sh-colhead-title">{c.name}</h1>
        {c.tagline && <p className="sh-hero-text">{c.tagline}</p>}
        {paragraphs.length > 0 && (
          <div className="sh-colhead-story">
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        )}
        {c.teased && (
          <div className="sh-colhead-drop">
            <CountdownDigits to={c.launchAt} onDone={onDrop} />
            <p className="sh-hero-when">{dropDate(c.launchAt)} · Prague time</p>
            <NotifyForm topic={`drop:${c.slug}`} cta="Notify me" compact />
          </div>
        )}
      </div>
    </header>
  );
}

function GridSkeleton() {
  return (
    <div className="sh-grid" aria-hidden="true">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="sh-card sh-skel">
          <div className="sh-card-media" />
          <div className="sh-card-body">
            <span className="sh-skel-line" />
            <span className="sh-skel-line short" />
          </div>
        </div>
      ))}
    </div>
  );
}

// What a careful buyer checks before paying, said once, plainly. The CMS can
// replace the built-in four.
const TRUST_ICONS = ["box", "truck", "returns", "lock", "mail", "pin", "check"];

export function TrustStrip({ items: custom = [] }) {
  const d = useDeliveryFacts();
  const builtIn = [
    {
      icon: "box",
      title: "Shipped from Prague",
      text: `Zásilkovna pickup point or home delivery${d.fromMinor != null ? `, from ${money(d.fromMinor)}` : ""}${d.freeOverMinor != null ? `. Free over ${money(d.freeOverMinor)}` : ""}.`,
    },
    { icon: "returns", title: `${d.returnDays}-day returns`, text: "Changed your mind? Send it back within " + d.returnDays + " days of delivery." },
    { icon: "lock", title: "Secure payment", text: d.card ? "Card, Apple Pay or Google Pay via SumUp, or a bank transfer QR code." : "Bank transfer with a QR code for your banking app." },
    { icon: "mail", title: "Real people", text: "Questions about sizing or an order? Write to info@thecreaksofficial.com." },
  ];
  const items = custom.length ? custom : builtIn;
  return (
    <section className="sh-trust" aria-label="Delivery, returns and payment">
      <div className="sh-wrap sh-trust-grid">
        {items.map((i) => (
          <div key={i.title} className="sh-trust-item">
            <Icon name={TRUST_ICONS.includes(i.icon) ? i.icon : "check"} size={22} />
            <h3>{i.title}</h3>
            {i.text && <p>{i.text}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}
